/*
 * The terms of sale / salgsvilkår for an arbiter account, and the modal that
 * shows them.
 *
 * Built the same way as the privacy policy (privacy-policy.js), for the same
 * reason: a legal text is content in both languages, section by section, not
 * markup that goes through the no.js dictionary.
 *
 * WHERE THEY ARE LINKED FROM. Settings (Privacy and Terms), the plan chooser,
 * the Subscription section, Stripe Checkout (the note above the pay button and
 * the "Public details" of the Stripe account), and the order confirmation
 * email. That last two need an address, so `zugzwang.no/?vilkar` opens these
 * terms and `zugzwang.no/?personvern` the privacy policy (openLegalFromUrl).
 *
 * KEEP IN STEP WITH: the prices and trial in functions/lib/billing.js (PLANS,
 * TRIAL_DAYS — functions/test/billing.test.js checks the prices appear here),
 * the angrerett wording in withdrawal-info.js and functions/lib/billing-emails.js,
 * and who the seller is in seller.js. Change UPDATED below whenever the text
 * changes, and tell subscribers about material changes (section 13).
 */

import { SELLER, sellerAddress, sellerName } from './seller.js';
import { openPrivacyPolicy } from './privacy-policy.js';

/** Shown at the top. Update whenever the text below changes. */
const UPDATED = { en: '6 October 2026', no: '6. oktober 2026' };

/*
 * A section is { h, body }; each entry in `body` is a paragraph (a string) or a
 * bulleted list (an array of strings or [term, text] pairs), as in the privacy
 * policy. {seller}, {address} and {email} are filled in from seller.js.
 */
const TERMS = {
    no: {
        lead: 'Disse vilkårene gjelder når du kjøper abonnement på en turneringslederkonto i Zugzwang. Les dem sammen med personvernerklæringen, som forteller hva vi gjør med personopplysninger.',
        sections: [
            {
                h: '1. Hvem du handler med',
                body: [
                    'Du inngår avtalen med {seller}, {address}. Zugzwang drives som et enkeltpersonforetak. Du når oss på {email} eller telefon {phone}.'
                ]
            },
            {
                h: '2. Hva du kjøper',
                body: [
                    'En turneringslederkonto gir tilgang til å opprette og drive sjakkturneringer i Zugzwang: deltakerlister og påmelding, trekning av runder, resultater og kvalitetsberegning, publisering og eksport, spillerdatabaser og samarbeid med andre turneringsledere. Tjenesten brukes i nettleseren på zugzwang.no. Abonnementet gjelder én konto, for én person.',
                    'Vi videreutvikler tjenesten løpende. Gjør vi en endring som har mer enn liten negativ betydning for deg, sier vi fra på forhånd, og du kan da si opp avtalen.'
                ]
            },
            {
                h: '3. Priser og betaling',
                body: [
                    [
                        ['Månedlig', '49 kr per måned'],
                        ['Hver 3. måned', '119 kr per 3. måned'],
                        ['Årlig', '349 kr per år']
                    ],
                    'Prisene er totalpriser i norske kroner. Zugzwang er ikke registrert i Merverdiavgiftsregisteret, så det kommer ingen merverdiavgift i tillegg, og heller ingen andre gebyrer.',
                    'Du betaler med kort gjennom betalingstjenesten Stripe. Kortopplysningene går direkte til Stripe, og vi ser dem aldri. Beløpet trekkes ved starten av hver periode, og du får kvittering på e-post for hver betaling.'
                ]
            },
            {
                h: '4. Gratis prøveperiode',
                body: [
                    'Første gang du abonnerer, er de første 14 dagene gratis. Sier du opp før prøveperioden er over, blir du ikke trukket noe. Prøveperioden gis én gang per konto.'
                ]
            },
            {
                h: '5. Når avtalen er inngått',
                body: [
                    'Avtalen er inngått når du bekrefter bestillingen på Stripes betalingsside. Du får en ordrebekreftelse på e-post med hva du har kjøpt, prisen, opplysninger om angreretten, angreskjemaet og en lenke til disse vilkårene.'
                ]
            },
            {
                h: '6. Fornyelse, bytte og oppsigelse',
                body: [
                    'Abonnementet fornyes automatisk for en ny periode av samme lengde til du sier det opp. Du kan si opp når som helst under Innstillinger → Abonnement → Administrer abonnement, eller ved å skrive til {email}. Oppsigelsen gjelder fra slutten av perioden du allerede har betalt for, og du beholder tilgangen ut perioden.',
                    'Samme sted kan du bytte mellom månedlig, kvartalsvis og årlig abonnement. Du ser hva byttet betyr for prisen før du bekrefter det.',
                    'Utenom angreretten (punkt 7) og ved feil og mangler (punkt 11) betaler vi ikke tilbake for en periode som er påbegynt.'
                ]
            },
            {
                h: '7. Angrerett',
                body: [
                    'Du har 14 dagers angrerett fra den dagen avtalen ble inngått, etter angrerettloven. Du bruker den med knappen «Angre kjøpet» under Innstillinger → Abonnement, eller ved å gi oss beskjed på en annen utvetydig måte, for eksempel på e-post til {email}. Du kan bruke angreskjemaet fra ordrebekreftelsen, men du må ikke.',
                    'Bruker du angreretten, betaler vi tilbake alt du har betalt, uten unødig opphold og senest 14 dager etter at vi fikk beskjeden, med samme betalingsmiddel som du brukte og uten gebyr.',
                    'I prøveperioden har du ikke betalt noe, så det koster ingenting å angre. Abonnerer du igjen etter at prøveperioden er brukt, starter abonnementet med en gang hvis du ber om det. Angrer du da, trekker vi fra et beløp som står i forhold til dagene du har hatt tjenesten.'
                ]
            },
            {
                h: '8. Når tilgangen tar slutt',
                body: [
                    'Når abonnementet er sagt opp og perioden er ute, når en betaling ikke går gjennom etter gjentatte forsøk, eller når du har brukt angreretten, blir kontoen din en spillerkonto. Turneringene, spillerdatabasene og de andre dataene dine blir liggende, og du får tilgang til dem igjen så snart du abonnerer på nytt.',
                    'Går en betaling ikke gjennom, prøver Stripe igjen over noen dager og sender deg e-post om det. Du beholder tilgangen så lenge Stripe prøver.',
                    'Vil du at dataene skal slettes, gjør du det med «Slett profilen min» under Innstillinger. Personvernerklæringen forteller hva som slettes, og hva som blir stående.',
                    'Sletter du profilen, sies abonnementet opp med en gang, og du blir ikke trukket mer. Som punkt 6 sier, betaler vi ikke tilbake for en periode som er påbegynt; har du fortsatt angrerett (punkt 7), bruker du den før du sletter profilen for å få pengene tilbake.'
                ]
            },
            {
                h: '9. Tilgangskoder',
                body: [
                    'En tilgangskode gir en turneringslederkonto gratis i perioden som gjelder for koden: én måned, tre måneder, ett år eller uten tidsbegrensning. Varer tilgangen en bestemt tid, sier vi fra på e-post en uke før den tar slutt. Vil du fortsette etter det, velger du et abonnement; ellers blir kontoen en spillerkonto som beskrevet i punkt 8.'
                ]
            },
            {
                h: '10. Prisendringer',
                body: [
                    'Endrer vi prisene, varsler vi deg på e-post minst 30 dager før endringen gjelder for deg. Den nye prisen gjelder fra den første perioden som starter etter at varselet er gitt, og du kan si opp før den tid hvis du ikke vil fortsette.'
                ]
            },
            {
                h: '11. Feil og mangler',
                body: [
                    'Virker ikke tjenesten slik den skal, gi oss beskjed på {email} så snart du kan. Du har rettigheter etter digitalytelsesloven, blant annet til å få feilen rettet, og til prisavslag eller å heve avtalen hvis den ikke blir rettet.',
                    'Vi gjør vårt beste for at tjenesten er tilgjengelig og fungerer, men korte avbrudd, for eksempel ved oppdateringer, kan forekomme. Vi er ikke ansvarlige for indirekte tap. Ingenting i disse vilkårene begrenser rettighetene du har etter ufravikelig forbrukerlovgivning.'
                ]
            },
            {
                h: '12. Personopplysninger',
                body: [
                    'Hvordan vi behandler personopplysninger, står i personvernerklæringen (zugzwang.no/?personvern). Betalingen behandles av Stripe.'
                ]
            },
            {
                h: '13. Endringer i vilkårene',
                body: [
                    'Vi kan endre disse vilkårene. Vesentlige endringer varsler vi på e-post minst 30 dager før de gjelder for deg, og du kan si opp før den tid. Datoen øverst viser når vilkårene sist ble endret.'
                ]
            },
            {
                h: '14. Klager og tvister',
                body: [
                    'Ta kontakt med oss først, på {email}; som regel finner vi en løsning. Blir vi ikke enige, kan du be Forbrukertilsynet om mekling, og saken kan deretter bringes inn for Forbrukerklageutvalget. Avtalen følger norsk rett.'
                ]
            },
            {
                h: 'Kontakt',
                body: [
                    '{seller}\n{address}\n{phone}\n{email}'
                ]
            }
        ]
    },
    en: {
        lead: 'These terms apply when you buy a subscription to an arbiter account in Zugzwang. Read them together with the privacy policy, which explains what we do with personal data.',
        sections: [
            {
                h: '1. Who you are dealing with',
                body: [
                    'You enter into the agreement with {seller}, {address}. Zugzwang is run as a sole proprietorship (enkeltpersonforetak). You can reach us at {email} or by phone on {phone}.'
                ]
            },
            {
                h: '2. What you are buying',
                body: [
                    'An arbiter account lets you create and run chess tournaments in Zugzwang: player lists and registration, pairing rounds, results and tie-breaks, publishing and export, player databases and working with other arbiters. The service is used in the browser at zugzwang.no. A subscription covers one account, for one person.',
                    'We keep developing the service. If we make a change that affects you negatively in more than a minor way, we tell you in advance, and you may then end the agreement.'
                ]
            },
            {
                h: '3. Prices and payment',
                body: [
                    [
                        ['Monthly', '49 kr a month'],
                        ['Every 3 months', '119 kr every 3 months'],
                        ['Yearly', '349 kr a year']
                    ],
                    'Prices are total prices in Norwegian kroner. Zugzwang is not registered for VAT (MVA), so no VAT is added, and there are no other fees.',
                    'You pay by card through the payment service Stripe. Your card details go straight to Stripe, and we never see them. The amount is charged at the start of each period, and you get a receipt by email for every payment.'
                ]
            },
            {
                h: '4. Free trial',
                body: [
                    'The first time you subscribe, the first 14 days are free. Cancel before the trial ends and nothing is charged. The trial is given once per account.'
                ]
            },
            {
                h: '5. When the agreement is made',
                body: [
                    'The agreement is made when you confirm the order on Stripe\'s payment page. You get an order confirmation by email with what you bought, the price, information about the right of withdrawal, the withdrawal form and a link to these terms.'
                ]
            },
            {
                h: '6. Renewal, switching and cancelling',
                body: [
                    'The subscription renews automatically for another period of the same length until you cancel it. You can cancel at any time under Settings → Subscription → Manage Subscription, or by writing to {email}. Cancelling takes effect at the end of the period you have already paid for, and you keep access until then.',
                    'In the same place you can switch between the monthly, quarterly and yearly plans. You see what the switch means for the price before you confirm it.',
                    'Apart from the right of withdrawal (section 7) and faults (section 11), we do not refund a period that has started.'
                ]
            },
            {
                h: '7. Right of withdrawal (angrerett)',
                body: [
                    'You have 14 days to withdraw from the day the agreement was made, under the Norwegian Right of Withdrawal Act (angrerettloven). You do it with the "Withdraw from purchase" button under Settings → Subscription, or by telling us in another unequivocal way, for example by email to {email}. You may use the withdrawal form from the order confirmation, but you do not have to.',
                    'If you withdraw, we refund everything you have paid, without undue delay and at the latest 14 days after we received your notice, to the means of payment you used and without any fee.',
                    'During the trial you have paid nothing, so withdrawing costs nothing. If you subscribe again after the trial has been used, the subscription starts straight away if you ask for that. If you then withdraw, we keep an amount proportionate to the days you have had the service.'
                ]
            },
            {
                h: '8. When access ends',
                body: [
                    'When the subscription has been cancelled and the period is over, when a payment fails after repeated attempts, or when you have used the right of withdrawal, your account becomes a player account. Your tournaments, player databases and other data stay where they are, and you have them back as soon as you subscribe again.',
                    'If a payment fails, Stripe tries again over a few days and emails you about it. You keep access while Stripe is trying.',
                    'To have your data deleted, use "Delete my profile" under Settings. The privacy policy explains what is deleted and what stays.',
                    'Deleting your profile cancels the subscription straight away, and you are not charged again. As section 6 says, a period that has started is not refunded; if you can still withdraw (section 7), do that before you delete your profile to get your money back.'
                ]
            },
            {
                h: '9. Access codes',
                body: [
                    'An access code gives an arbiter account free of charge for the period that applies to the code: one month, three months, one year or without time limit. If the access lasts a set time, we let you know by email a week before it ends. To carry on after that, choose a plan; otherwise the account becomes a player account as described in section 8.'
                ]
            },
            {
                h: '10. Price changes',
                body: [
                    'If we change the prices, we tell you by email at least 30 days before the change applies to you. The new price applies from the first period that starts after the notice, and you may cancel before then if you do not want to continue.'
                ]
            },
            {
                h: '11. Faults',
                body: [
                    'If the service does not work as it should, let us know at {email} as soon as you can. You have rights under the Norwegian Digital Services Act (digitalytelsesloven), among them to have the fault put right, and to a price reduction or to end the agreement if it is not.',
                    'We do our best to keep the service available and working, but short interruptions, for example during updates, can happen. We are not liable for indirect loss. Nothing in these terms limits the rights you have under mandatory consumer law.'
                ]
            },
            {
                h: '12. Personal data',
                body: [
                    'How we handle personal data is set out in the privacy policy (zugzwang.no/?personvern). Payments are processed by Stripe.'
                ]
            },
            {
                h: '13. Changes to these terms',
                body: [
                    'We may change these terms. We tell you about material changes by email at least 30 days before they apply to you, and you may cancel before then. The date at the top shows when the terms last changed.'
                ]
            },
            {
                h: '14. Complaints and disputes',
                body: [
                    'Contact us first, at {email}; we usually find a solution. If we cannot agree, you can ask the Norwegian Consumer Authority (Forbrukertilsynet) to mediate, and the case can then be brought before the Consumer Complaints Committee (Forbrukerklageutvalget). The agreement is governed by Norwegian law.'
                ]
            },
            {
                h: 'Contact',
                body: [
                    '{seller}\n{address}\n{phone}\n{email}'
                ]
            }
        ]
    }
};

const LABELS = {
    en: { updated: 'Last updated: {date}' },
    no: { updated: 'Sist oppdatert: {date}' }
};

function fill(text) {
    return String(text)
        .replace(/\{seller\}/g, sellerName())
        .replace(/\{address\}/g, sellerAddress())
        .replace(/\{email\}/g, SELLER.email)
        .replace(/\{phone\}/g, SELLER.phone);
}

function activeLang() {
    const lang = window.i18n ? window.i18n.getLang() : 'no';
    return TERMS[lang] ? lang : 'no';
}

function paragraph(text) {
    const p = document.createElement('p');
    p.style.cssText = 'margin: 0 0 0.85rem; color: var(--text-muted); line-height: 1.6;';
    fill(text).split('\n').forEach((line, i) => {
        if (i > 0) p.appendChild(document.createElement('br'));
        p.appendChild(document.createTextNode(line));
    });
    return p;
}

function list(items) {
    const ul = document.createElement('ul');
    ul.style.cssText = 'margin: 0 0 0.85rem; padding-left: 1.25rem; color: var(--text-muted); line-height: 1.6;';
    items.forEach(item => {
        const li = document.createElement('li');
        li.style.marginBottom = '0.4rem';
        if (Array.isArray(item)) {
            const term = document.createElement('strong');
            term.style.color = 'var(--text-main)';
            term.textContent = fill(item[0]);
            li.appendChild(term);
            li.appendChild(document.createTextNode(' — ' + fill(item[1])));
        } else {
            li.textContent = fill(item);
        }
        ul.appendChild(li);
    });
    return ul;
}

/** Built on every open, so a language switch mid-session shows the right text. */
function render(container) {
    const lang = activeLang();
    const doc = TERMS[lang];
    container.textContent = '';

    const updated = document.createElement('p');
    updated.style.cssText = 'margin: 0 0 1rem; color: var(--text-muted); font-size: 0.85rem;';
    updated.textContent = LABELS[lang].updated.replace('{date}', UPDATED[lang]);
    container.appendChild(updated);
    container.appendChild(paragraph(doc.lead));

    doc.sections.forEach(section => {
        const h = document.createElement('h3');
        h.style.cssText = 'margin: 1.5rem 0 0.6rem; font-size: 1.05rem; font-weight: 600; color: var(--text-main);';
        h.textContent = section.h;
        container.appendChild(h);
        section.body.forEach(block => {
            container.appendChild(Array.isArray(block) ? list(block) : paragraph(block));
        });
    });
}

export function openTermsOfSale() {
    const modal = document.getElementById('terms-modal');
    const body = document.getElementById('terms-modal-body');
    if (!modal || !body) return;
    render(body);
    body.scrollTop = 0;
    modal.classList.add('active');
}

export function closeTermsOfSale() {
    const modal = document.getElementById('terms-modal');
    if (modal) modal.classList.remove('active');
}

/**
 * `?vilkar` (or `?terms`) opens these terms, `?personvern` (or `?privacy`) the
 * privacy policy: the addresses Stripe's settings and the emails link to.
 *
 * Waits for the app to come out from behind its loading screen, so the text is
 * drawn in the language the app settles on; the parameter is then taken off
 * the address, so a reload does not open it again.
 */
function openLegalFromUrl() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    const terms = params.has('vilkar') || params.has('terms');
    const privacy = params.has('personvern') || params.has('privacy');
    if (!terms && !privacy) return;

    ['vilkar', 'terms', 'personvern', 'privacy'].forEach(k => params.delete(k));
    try {
        const qs = params.toString();
        window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    } catch (e) { /* the page still opens */ }

    const open = () => (terms ? openTermsOfSale() : openPrivacyPolicy());
    const started = Date.now();
    const poll = setInterval(() => {
        if (!document.body.classList.contains('loading') || Date.now() - started > 8000) {
            clearInterval(poll);
            open();
        }
    }, 150);
}

/** Its own close paths, as the privacy policy has: the X, the footer button, the backdrop, Escape. */
export function setupTermsOfSale() {
    const openBtn = document.getElementById('btn-terms-of-sale');
    if (openBtn) openBtn.addEventListener('click', openTermsOfSale);
    document.querySelectorAll('.link-terms-of-sale').forEach(link => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            openTermsOfSale();
        });
    });

    const modal = document.getElementById('terms-modal');
    if (modal) {
        modal.querySelectorAll('.btn-close-terms').forEach(btn => btn.addEventListener('click', closeTermsOfSale));
        modal.addEventListener('click', (e) => { if (e.target === modal) closeTermsOfSale(); });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('active')) closeTermsOfSale();
        });
    }

    openLegalFromUrl();
}
